import React, { useMemo, useState } from 'react';
import AssignmentSubmission from 'models/AssignmentSubmission';
import { useClassroomContext } from 'contexts/ClassroomContext';
import AssignmentSubmissionRow from 'view/components/teacher/classroom/AssignmentSubmissionRow';
import TeacherGradebook from 'models/TeacherGradebook';
import Assignment from 'models/Assignment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartSimple,
  faGraduationCap,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { SearchBar } from 'view/components/common/SearchBar';
import IconButton from 'view/components/buttons/IconButton';
import useApiCall from 'contexts/ApiCall';
import teacherService from 'services/teacherService';
import { useGradebook } from 'contexts/TeacherGradebookContext';
import { useNavigate } from 'react-router-dom';

interface AssignmentSubmissionsProps {
  assignment: Assignment;
  gradebook: TeacherGradebook;
  onUpdateAssignmentSubmission: (
    assignment: AssignmentSubmission,
    field: string,
    value: any,
  ) => void;
  onClose: () => void;
}

export const AssignmentSubmissions: React.FC<AssignmentSubmissionsProps> = ({
  assignment,
  gradebook,
  onUpdateAssignmentSubmission,
  onClose,
}) => {
  const { classroom } = useClassroomContext();
  const { setGradebook } = useGradebook();
  const [selectedAssignment, setSelectedAssignment] = useState<string>();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const navigate = useNavigate();
  const makeApiCall = useApiCall();

  const handleSearchSubmissions = (searchTerm: string) => {
    setSearchTerm(searchTerm.toLowerCase());
  };

  const visibleStudents = useMemo(
    () =>
      gradebook.students.slice().filter((student) => {
        const submission = student.submissions.find(
          (sa) => Number(sa.assignment) === Number(assignment.getId()),
        );
        return (
          submission && student.displayName().toLowerCase().includes(searchTerm)
        );
      }),
    [searchTerm, assignment, gradebook],
  );

  const handleDeleteAssignment = () => {
    makeApiCall(teacherService.deleteAssignment, assignment.id)
      .then((resp) => {
        setGradebook((prev) => prev?.removeAssignment(assignment));
        onClose();
      })
      .catch((error) => alert('Failed to delete the assignment'));
  };

  const handleViewAssignmentAnalytics = () => {
    if (!classroom) return;
    navigate(
      `/teacher/classroom/${classroom.id}/insights/?assignment=${assignment.id}`,
    );
  };

  if (!classroom) return null;

  return (
    <div className="student-details">
      <div className="student-details-header">
        <div className="row spaced">
          <div className="row">
            <FontAwesomeIcon
              icon={faGraduationCap}
              size="xl"
              color="var(--pure-white)"
            />
            <label className="label-large">{assignment.title}</label>
            <IconButton
              icon={faChartSimple}
              onClick={handleViewAssignmentAnalytics}
            />
          </div>
          <IconButton
            type="transparent"
            icon={faTrash}
            color="var(--pure-white)"
            onClick={handleDeleteAssignment}
            requiresConfirmation={true}
            confirmationMessage="Are you sure you want to permanently delete this assignment and all associated data?"
          />
        </div>
      </div>
      <div className="student-details-content">
        <SearchBar onSearch={handleSearchSubmissions} />
        {visibleStudents.map((student) => {
          const assignmentSubmission = student.submissions.find(
            (sa) => Number(sa.assignment) === Number(assignment.getId()),
          );
          return (
            assignmentSubmission && (
              <AssignmentSubmissionRow
                key={`student-assignment-row-${student.displayName()}`}
                label={student.displayName()}
                assignmentSubmission={assignmentSubmission}
                assignment={assignment}
                selected={selectedAssignment === assignmentSubmission.id}
                setSelected={setSelectedAssignment}
                onUpdateAssignmentSubmission={onUpdateAssignmentSubmission}
              />
            )
          );
        })}
      </div>
    </div>
  );
};

export default AssignmentSubmissions;
